import i18next from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** 법인인감 날인 신청 — 레거시 appr_edit_forms_seal */
export const CORPORATE_SEAL_TYPE = 113;

export const isCorporateSealType = (apvType: number): boolean =>
  apvType === CORPORATE_SEAL_TYPE;

type SealOption = {
  value: string;
  label: string;
  /** 레거시가 저장한 다른 표기 — 편집 복원 비교용 */
  legacyLabels?: string[];
};

export const SEAL_COMPANY_OPTIONS: SealOption[] = [
  { value: '1', label: 'F&F' },
  { value: '2', label: 'F&F홀딩스', legacyLabels: ['F&FHoldings'] },
  { value: '3', label: 'F&F로지스틱스', legacyLabels: ['F&FLogistics'] },
  { value: '4', label: 'F&F엔터테인먼트', legacyLabels: ['F&FEntertainment'] },
  { value: '5', label: 'F&F파트너스', legacyLabels: ['F&FPartners'] },
  {
    value: '6',
    label: 'F&F파트너스1호조합',
    legacyLabels: ['F&FPartners No.1 Investment Association'],
  },
  {
    value: '7',
    label: '에프앤에프파트너스 사제 인공지능 투자조합',
    legacyLabels: ['FNFPARTNERS SAZZE AI FUND'],
  },
  { value: '8', label: 'F&CO' },
];

const SEAL_RAW = { value: 'seal_raw', label: '법인인감' };
const sealUse = (n: number) => ({
  value: `seal_use${n}`,
  label: `${n}번 사용인감`,
});
const sealSenceUse = (n: number) => ({
  value: `seal_sence_use${n}`,
  label: `${n}번 사용인감계`,
});

/** 법인별 인감 종류 — 법인을 바꾸면 목록이 바뀜 */
export const SEAL_OPTIONS_BY_COMPANY: Readonly<Record<string, SealOption[]>> = {
  '1': [
    SEAL_RAW,
    sealUse(1),
    sealUse(5),
    sealUse(7),
    sealSenceUse(1),
    sealSenceUse(5),
  ],
  '2': [SEAL_RAW, sealUse(1), sealUse(3), sealUse(5)],
  '3': [SEAL_RAW, sealUse(1), sealUse(3), sealUse(4)],
  '4': [SEAL_RAW, sealUse(1), sealUse(3)],
  '5': [
    SEAL_RAW,
    sealUse(1),
    sealUse(2),
    { value: 'seal_use', label: '사용인감 (◇)' },
  ],
  '6': [SEAL_RAW, sealUse(1), { value: 'seal_use', label: '사용인감 (☆)' }],
  '7': [sealUse(1), sealUse(2)],
  '8': [SEAL_RAW, { value: 'seal_use', label: '사용인감' }],
};

export const LAW_CONFIRM_OPTIONS: SealOption[] = [
  { value: 'reviewNotYet', label: '미검토', legacyLabels: ['reviewNotYet'] },
  { value: 'reviewEnd', label: '검토 완료', legacyLabels: ['reviewEnd'] },
];

export type CorporateSealFormState = {
  company: string;
  seal: string;
  lawConfirm: string;
};

export type CorporateSealAttributes = {
  company: string;
  seal: string;
  lawConfirm: string;
};

export const sealOptionsOf = (company: string): SealOption[] =>
  SEAL_OPTIONS_BY_COMPANY[company] ?? [];

/** 작성자 언어 라벨 — 번역 리소스 로드 전이면 원문(한국어 키)으로 */
const localize = (key: string): string => i18next.t(key) || key;

const labelOf = (options: SealOption[], value: string): string => {
  const found = options.find((o) => o.value === value);
  return found ? localize(found.label) : '';
};

export const createCorporateSealState = (): CorporateSealFormState => ({
  company: SEAL_COMPANY_OPTIONS[0].value,
  seal: sealOptionsOf(SEAL_COMPANY_OPTIONS[0].value)[0].value,
  lawConfirm: LAW_CONFIRM_OPTIONS[0].value,
});

/** 법인 변경 — 인감 종류는 새 목록의 첫 항목으로 */
export const changeSealCompany = (
  state: CorporateSealFormState,
  company: string,
): CorporateSealFormState => ({
  ...state,
  company,
  seal: sealOptionsOf(company)[0]?.value ?? '',
});

/** 셀렉트를 바꿀 때마다 다시 씀 — 직접 고친 제목도 덮어씀(레거시 동일) */
export const buildCorporateSealSubject = (
  state: CorporateSealFormState,
): string =>
  `${localize('법인인감 날인 신청의 건')} (${labelOf(
    SEAL_COMPANY_OPTIONS,
    state.company,
  )} / ${labelOf(sealOptionsOf(state.company), state.seal)})`;

/** 저장값은 코드가 아니라 고른 항목의 표시 글자(레거시 동일) */
export const buildCorporateSealAttributes = (
  state: CorporateSealFormState | null | undefined,
): ApprovalAttributes | null =>
  state
    ? {
        company: labelOf(SEAL_COMPANY_OPTIONS, state.company),
        seal: labelOf(sealOptionsOf(state.company), state.seal),
        lawConfirm: labelOf(LAW_CONFIRM_OPTIONS, state.lawConfirm),
      }
    : null;

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export const parseCorporateSealAttributes = (
  attributes: unknown,
): CorporateSealAttributes | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const a = attributes as Record<string, unknown>;
  const parsed = {
    company: str(a.company),
    seal: str(a.seal),
    lawConfirm: str(a.lawConfirm),
  };
  return parsed.company || parsed.seal || parsed.lawConfirm ? parsed : null;
};

const LANGS = ['ko', 'en', 'zh'] as const;

/** 저장 글자를 어느 언어로 저장했든 같은 항목으로 찾음 */
const findByLabel = (options: SealOption[], saved: string): string | null => {
  if (!saved) return null;
  const found = options.find(
    (o) =>
      o.label === saved ||
      o.legacyLabels?.includes(saved) ||
      (i18next.isInitialized &&
        LANGS.some((lng) => i18next.getFixedT(lng)(o.label) === saved)),
  );
  return found?.value ?? null;
};

/** 편집 진입 — 못 찾은 항목은 각 셀렉트의 첫 항목(레거시 동일) */
export const corporateSealAttributesToState = (
  saved: CorporateSealAttributes | null,
): CorporateSealFormState => {
  const initial = createCorporateSealState();
  if (!saved) return initial;
  const company =
    findByLabel(SEAL_COMPANY_OPTIONS, saved.company) ?? initial.company;
  const sealOptions = sealOptionsOf(company);
  return {
    company,
    seal: findByLabel(sealOptions, saved.seal) ?? sealOptions[0]?.value ?? '',
    lawConfirm:
      findByLabel(LAW_CONFIRM_OPTIONS, saved.lawConfirm) ?? initial.lawConfirm,
  };
};

export const corporateSealKey = (
  state: CorporateSealFormState | null,
): string =>
  state ? JSON.stringify([state.company, state.seal, state.lawConfirm]) : '';
